import { useEffect, useRef, useState } from 'react';
import { v } from '../constants';
import { computeConstraintStatuses } from '../rules';
import { SharedHeader } from './SharedHeader';
import { HexBoard } from './HexBoard';
import { useAuth } from '../auth/AuthContext';
import {
  fetchCustomPuzzle,
  fetchCustomPuzzleList,
  fetchMyCustomSolvedIds,
  type CustomPuzzleFetch,
  type CustomPuzzleSummary,
  type CustomSort,
  type SizeFilter,
} from '../lib/customPuzzles';

// Public discovery feed for community-authored custom puzzles. Three
// sort modes (Recent / Top-rated / Most played). Anonymous OK.

const SORT_TABS: { value: CustomSort; label: string; hint: string }[] = [
  { value: 'recent', label: 'Recent',     hint: 'Newest first' },
  { value: 'top',    label: 'Top-rated',  hint: 'Best up/down ratio' },
  { value: 'played', label: 'Most played', hint: 'Highest play count' },
];

const PAGE_SIZE = 20;

const SIZE_FILTERS: { value: SizeFilter; label: string; cells: string }[] = [
  { value: null, label: 'All sizes', cells: '' },
  { value: 2,    label: 'Small',     cells: '7' },
  { value: 3,    label: 'Medium',    cells: '19' },
  { value: 4,    label: 'Large',     cells: '37' },
];

// Hover-preview tuning. 200ms delay before fetching keeps the network
// quiet on quick scroll-through. 900px viewport floor — under that we
// don't have horizontal room to render a popover.
const HOVER_DELAY_MS = 200;
const HOVER_MIN_VIEWPORT = 900;
const PREVIEW_WIDTH = 240;
const PREVIEW_HEIGHT = 220;

export function PuzzleBrowse() {
  const auth = useAuth();
  const [sort, setSort] = useState<CustomSort>('recent');
  const [sizeFilter, setSizeFilter] = useState<SizeFilter>(null);
  const [puzzles, setPuzzles] = useState<CustomPuzzleSummary[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [solvedIds, setSolvedIds] = useState<Set<string>>(new Set());

  // Lazy-fetched preview cache, hovered row id, and screen-position
  // anchor for the popover. We resolve position from getBoundingClientRect
  // when hover starts and freeze it for the duration of the hover so the
  // popover doesn't twitch as the cursor moves over the row.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [previewPos, setPreviewPos] = useState<{ left: number; top: number } | null>(null);
  const previewCacheRef = useRef<Map<string, CustomPuzzleFetch>>(new Map());
  const [previewCacheVersion, setPreviewCacheVersion] = useState(0);
  const hoverTimerRef = useRef<number | null>(null);

  // Pull the set of puzzle ids the signed-in user has solved so rows
  // can render a "✓ Solved" marker. One-shot fetch; refreshes if the
  // user signs in/out mid-session.
  useEffect(() => {
    if (!auth.user) { setSolvedIds(new Set()); return; }
    let cancelled = false;
    (async () => {
      const ids = await fetchMyCustomSolvedIds();
      if (cancelled || !ids) return;
      setSolvedIds(new Set(ids));
    })();
    return () => { cancelled = true; };
  }, [auth.user]);

  useEffect(() => {
    // Reset when sort or size filter changes
    setPuzzles([]);
    setOffset(0);
    setHasMore(true);
    setLoading(true);
    setError(null);
    (async () => {
      const data = await fetchCustomPuzzleList(sort, PAGE_SIZE, 0, sizeFilter);
      if (!data) { setError('Couldn\'t load puzzles. Check your connection and refresh.'); setLoading(false); return; }
      setPuzzles(data.puzzles);
      setOffset(data.puzzles.length);
      setHasMore(data.puzzles.length >= PAGE_SIZE);
      setLoading(false);
    })();
  }, [sort, sizeFilter]);

  async function loadMore() {
    setLoading(true);
    const data = await fetchCustomPuzzleList(sort, PAGE_SIZE, offset, sizeFilter);
    setLoading(false);
    if (!data) { setError("Couldn't load more puzzles."); return; }
    setPuzzles(prev => [...prev, ...data.puzzles]);
    setOffset(offset + data.puzzles.length);
    setHasMore(data.puzzles.length >= PAGE_SIZE);
  }

  function handleRowEnter(id: string, e: React.MouseEvent<HTMLAnchorElement>) {
    if (typeof window === 'undefined') return;
    if (window.innerWidth < HOVER_MIN_VIEWPORT) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // Try to position the popover to the right of the row; flip to the
    // left if there isn't room. Clamp top to stay in viewport.
    const wantRightEdge = rect.right + 12 + PREVIEW_WIDTH;
    const left = wantRightEdge <= window.innerWidth
      ? rect.right + 12
      : Math.max(8, rect.left - 12 - PREVIEW_WIDTH);
    const top = Math.max(8, Math.min(window.innerHeight - PREVIEW_HEIGHT - 8, rect.top));
    setPreviewPos({ left, top });
    if (hoverTimerRef.current != null) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(async () => {
      setHoveredId(id);
      if (!previewCacheRef.current.has(id)) {
        const data = await fetchCustomPuzzle(id);
        if (data) {
          previewCacheRef.current.set(id, data);
          setPreviewCacheVersion(prev => prev + 1);
        }
      }
    }, HOVER_DELAY_MS);
  }

  function handleRowLeave() {
    if (hoverTimerRef.current != null) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHoveredId(null);
    setPreviewPos(null);
  }

  // Clean up the pending timer when the component unmounts so a
  // late-resolving fetch doesn't try to update unmounted state.
  useEffect(() => () => {
    if (hoverTimerRef.current != null) window.clearTimeout(hoverTimerRef.current);
  }, []);

  const hoveredPreview = hoveredId ? previewCacheRef.current.get(hoveredId) ?? null : null;
  // Suppress unused warning — previewCacheVersion exists to force a
  // re-render when the cache entry lands after the fetch completes.
  void previewCacheVersion;

  return (
    <div className="hm-browse">
      <SharedHeader activeView="browse" />

      <div className="hm-browse-tabs" role="tablist">
        {SORT_TABS.map(t => (
          <button
            key={t.value}
            role="tab"
            aria-selected={sort === t.value}
            className={`hm-browse-tab ${sort === t.value ? 'active' : ''}`}
            onClick={() => setSort(t.value)}
            title={t.hint}
          >{t.label}</button>
        ))}
      </div>

      <div className="hm-browse-filters" role="group" aria-label="Filter by size">
        {SIZE_FILTERS.map(f => (
          <button
            key={String(f.value)}
            type="button"
            className={`hm-browse-chip ${sizeFilter === f.value ? 'active' : ''}`}
            onClick={() => setSizeFilter(f.value)}
            aria-pressed={sizeFilter === f.value}
            title={f.cells ? `${f.cells} cells` : 'Show all'}
          >
            {f.label}
            {f.cells && <span className="hm-browse-chip-sub"> · {f.cells}</span>}
          </button>
        ))}
      </div>

      {error && <p className="hm-browse-error">{error}</p>}

      <ul className="hm-browse-list">
        {puzzles.map(p => (
          <li key={p.id}>
            <a
              href={`/p/${p.id}`}
              className="hm-browse-row"
              onMouseEnter={(e) => handleRowEnter(p.id, e)}
              onMouseLeave={handleRowLeave}
            >
              <div className="hm-browse-row-main">
                <div className="hm-browse-row-name">
                  {p.name}
                  <span className="hm-browse-row-sizebadge" title={`${cellsForSize(p.size)} cells`}>
                    {sizeLabel(p.size)}
                  </span>
                  {solvedIds.has(p.id) && (
                    <span className="hm-browse-row-solved" title="You've solved this puzzle">
                      ✓ Solved
                    </span>
                  )}
                </div>
                <div className="hm-browse-row-meta">
                  {p.author?.displayName ? (
                    <>by <strong>{p.author.displayName}</strong></>
                  ) : (
                    <em>by anonymous</em>
                  )}
                  <span className="hm-browse-row-dot">·</span>
                  <span>{p.playCount} {p.playCount === 1 ? 'solve' : 'solves'}</span>
                  {p.viewCount > 0 && (
                    <>
                      <span className="hm-browse-row-dot">·</span>
                      <span title={`${p.playCount} solves across ${p.viewCount} views`}>
                        {Math.min(100, Math.round((p.playCount / p.viewCount) * 100))}% completion
                      </span>
                    </>
                  )}
                  {p.avgSolveMs != null && (
                    <>
                      <span className="hm-browse-row-dot">·</span>
                      <span title="Average solve time">avg {formatTimeMs(p.avgSolveMs)}</span>
                    </>
                  )}
                  <span className="hm-browse-row-dot">·</span>
                  <span>{formatAgo(p.createdAt)}</span>
                </div>
              </div>
              <div className="hm-browse-row-thumbs">
                <span>👍 {p.thumbsUp}</span>
                <span>👎 {p.thumbsDown}</span>
              </div>
            </a>
          </li>
        ))}
      </ul>

      {!loading && puzzles.length === 0 && !error && (
        <p className="hm-browse-empty">No puzzles yet. Be the first to <a href="/designer">design one</a>.</p>
      )}

      {hasMore && !error && (
        <div className="hm-browse-more">
          <button type="button" className="hm-action" onClick={loadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {hoveredId && previewPos && (
        <div
          className="hm-browse-preview"
          style={{ left: previewPos.left, top: previewPos.top, width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
          aria-hidden="true"
        >
          {hoveredPreview ? (
            <PreviewBoard meta={hoveredPreview} />
          ) : (
            <div className="hm-browse-preview-loading">Loading preview…</div>
          )}
        </div>
      )}
    </div>
  );
}

// Non-interactive miniature of the puzzle board. We compute evaluated
// constraints from the static payload so chips render with their
// declared values. Empty clickable/draggable sets + no-op handlers
// keep the board inert (CSS also enforces pointer-events: none on the
// wrapping popover).
function PreviewBoard({ meta }: { meta: CustomPuzzleFetch }) {
  const cells = meta.payload.cells;
  const evaluatedConstraints = computeConstraintStatuses(cells, meta.payload.constraints);
  return (
    <div className="hm-browse-preview-board">
      <div className="hm-browse-preview-name">{meta.payload.name}</div>
      <HexBoard
        cells={cells}
        evaluatedConstraints={evaluatedConstraints}
        badRestr={new Set()}
        R={12}
        pad={28}
        gap={3}
        onCellClick={() => { /* preview is non-interactive */ }}
        onCellPointerDown={() => { /* preview is non-interactive */ }}
        clickableCells={new Set()}
        draggableCells={new Set()}
        recentlyPlaced={null}
        drag={null}
        dragOverIdx={null}
      />
    </div>
  );
}

function sizeLabel(size: number | undefined): string {
  switch (size) {
    case 2: return 'Small';
    case 4: return 'Large';
    default: return 'Medium';
  }
}

function cellsForSize(size: number | undefined): number {
  switch (size) {
    case 2: return 7;
    case 4: return 37;
    default: return 19;
  }
}

function formatTimeMs(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const seconds = Math.floor((Date.now() - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}
